import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function DeepHoleDrillingPage() {
  const capabilities = [
    {
      icon: 'ri-focus-3-line',
      title: 'Ultra-High Precision',
      description: 'Achieve drilling accuracy within ±0.005mm with our advanced CNC control systems and real-time monitoring technology.',
      specs: ['Positioning accuracy: ±0.005mm', 'Straightness tolerance: 0.02mm/100mm', 'Surface roughness: Ra 0.4-1.6μm']
    },
    {
      icon: 'ri-ruler-line',
      title: 'Deep Penetration',
      description: 'Industry-leading depth-to-diameter ratios up to 400:1, enabling complex applications that others cannot achieve.',
      specs: ['Maximum depth: 3000mm', 'Diameter range: φ0.5-30mm', 'Depth-to-diameter ratio: up to 400:1']
    },
    {
      icon: 'ri-compass-3-line',
      title: 'Minimal Deviation',
      description: 'Advanced coolant delivery and chip removal systems ensure straight holes with minimal deviation throughout the entire depth.',
      specs: ['Deviation control: &lt;0.1mm per 100mm', 'Consistent hole quality', 'Optimized coolant pressure']
    },
    {
      icon: 'ri-speed-line',
      title: 'High Efficiency',
      description: 'Automated processes and optimized cutting parameters reduce cycle times while maintaining superior quality standards.',
      specs: ['Reduced production time by 40%', 'Automated tool changing', 'Real-time process monitoring']
    }
  ];

  const applications = [
    {
      title: 'Hydraulic Cylinders',
      description: 'Precision drilling for hydraulic cylinder bores with exceptional straightness and surface finish requirements.',
      image: 'https://public.readdy.ai/ai/img_res/ac61b4ba4d47a4bc04602595f9b71032.jpg',
      features: ['Long bore lengths', 'Tight tolerances', 'Superior surface finish']
    },
    {
      title: 'Mold Cooling Channels',
      description: 'Create complex cooling channels in injection molds to optimize temperature control and reduce cycle times.',
      image: 'https://readdy.ai/api/search-image?query=Precision%20injection%20mold%20with%20cooling%20channels%20being%20drilled%20using%20advanced%20deep%20hole%20drilling%20technology%2C%20clean%20white%20background%2C%20professional%20industrial%20photography%2C%20metallic%20mold%20components%2C%20technical%20manufacturing%20environment&width=800&height=600&seq=dhd-mold-002&orientation=landscape',
      features: ['Complex geometries', 'Conformal cooling', 'Enhanced productivity']
    },
    {
      title: 'Aerospace Components',
      description: 'Meet stringent aerospace standards for critical components requiring deep, precise holes in exotic materials.',
      image: 'https://readdy.ai/api/search-image?query=Aerospace%20titanium%20components%20with%20precision%20deep%20holes%20being%20manufactured%20on%20advanced%20CNC%20drilling%20equipment%2C%20clean%20white%20background%2C%20professional%20industrial%20photography%2C%20high-tech%20aerospace%20parts%2C%20technical%20engineering%20setting&width=800&height=600&seq=dhd-aerospace-003&orientation=landscape',
      features: ['Exotic materials', 'Critical tolerances', 'Full traceability']
    },
    {
      title: 'Medical Devices',
      description: 'Ultra-precise drilling for medical instruments and implants with biocompatible materials and cleanroom standards.',
      image: 'https://readdy.ai/api/search-image?query=Medical%20device%20components%20with%20micro%20precision%20deep%20holes%20being%20manufactured%20in%20cleanroom%20environment%2C%20clean%20white%20background%2C%20professional%20medical%20manufacturing%20photography%2C%20stainless%20steel%20surgical%20instruments%2C%20sterile%20technical%20setting&width=800&height=600&seq=dhd-medical-004&orientation=landscape',
      features: ['Micro drilling', 'Biocompatible materials', 'Cleanroom processing']
    },
    {
      title: 'Oil &amp; Gas Equipment',
      description: 'Heavy-duty drilling for oil and gas industry components requiring large diameters and extreme depths.',
      image: 'https://readdy.ai/api/search-image?query=Heavy%20duty%20oil%20and%20gas%20equipment%20components%20with%20large%20diameter%20deep%20hole%20drilling%20on%20industrial%20machinery%2C%20clean%20white%20background%2C%20professional%20industrial%20photography%2C%20robust%20metallic%20parts%2C%20technical%20manufacturing%20environment&width=800&height=600&seq=dhd-oilgas-005&orientation=landscape',
      features: ['Large diameters', 'Extreme depths', 'Harsh environment materials']
    },
    {
      title: 'Automotive Industry',
      description: 'High-volume production drilling for engine blocks, transmission components, and fuel injection systems.',
      image: 'https://readdy.ai/api/search-image?query=Automotive%20engine%20block%20components%20with%20precision%20deep%20hole%20drilling%20for%20fuel%20injection%20systems%2C%20clean%20white%20background%2C%20professional%20automotive%20manufacturing%20photography%2C%20metallic%20engine%20parts%2C%20technical%20production%20environment&width=800&height=600&seq=dhd-automotive-006&orientation=landscape',
      features: ['High-volume production', 'Consistent quality', 'Fast cycle times']
    }
  ];

  const technologies = [
    {
      icon: 'ri-settings-3-line',
      title: 'Gun Drilling',
      description: 'Single-lip cutting tool with internal coolant delivery for straight, deep holes with excellent surface finish.',
      advantages: ['Depth-to-diameter ratios up to 400:1', 'Superior straightness', 'Excellent chip evacuation']
    },
    {
      icon: 'ri-contrast-2-line',
      title: 'BTA Drilling',
      description: 'Boring and Trepanning Association method for larger diameter holes with external chip removal system.',
      advantages: ['Diameters from 18mm to 250mm', 'High material removal rates', 'Reduced tool wear']
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Ejector Drilling',
      description: 'Double-tube system combining advantages of gun drilling and BTA for maximum efficiency in deep holes.',
      advantages: ['Optimal for medium diameters', 'High penetration rates', 'Excellent hole quality']
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Material Preparation',
      description: 'Workpiece is precisely positioned and secured with proper alignment to ensure optimal drilling accuracy.'
    },
    {
      step: '02',
      title: 'Tool Selection',
      description: 'Appropriate drilling tool is selected based on material type, hole diameter, depth requirements, and surface finish specifications.'
    },
    {
      step: '03',
      title: 'Parameter Optimization',
      description: 'Cutting speed, feed rate, and coolant pressure are optimized for the specific application to maximize efficiency and quality.'
    },
    {
      step: '04',
      title: 'Drilling Operation',
      description: 'Automated drilling process with real-time monitoring of cutting forces, vibration, and coolant flow to ensure consistent quality.'
    },
    {
      step: '05',
      title: 'Quality Inspection',
      description: 'Comprehensive inspection including dimensional accuracy, straightness, surface finish, and internal defect detection.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-[#001E3C] via-[#002A4E] to-[#001E3C] overflow-hidden">
        <div className="absolute inset-0">
          <div className="w-full h-full">
            <img
              src="https://readdy.ai/api/search-image?query=Advanced%20precision%20deep%20hole%20drilling%20CNC%20machinery%20in%20modern%20manufacturing%20facility%20with%20metallic%20components%20being%20processed%2C%20clean%20white%20background%2C%20professional%20industrial%20photography%20style%2C%20high-tech%20equipment%2C%20technical%20engineering%20environment%20with%20blue%20accent%20lighting&width=1920&height=800&seq=dhd-hero-main&orientation=landscape"
              alt="Deep Hole Drilling Technology"
              className="w-full h-full object-cover object-top opacity-20"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#001E3C]"></div>
        </div>

        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#00D9C9]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#0066FF]/10 rounded-full blur-3xl"></div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-[#00D9C9]/10 border border-[#00D9C9] rounded-full px-4 py-2 mb-6">
              <i className="ri-tools-line text-[#00D9C9]"></i>
              <span className="text-[#00D9C9] text-sm font-semibold uppercase tracking-wider">Core Technology</span>
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Deep Hole Drilling Technology
            </h1>

            <p className="text-xl lg:text-2xl text-white/80 mb-8 leading-relaxed">
              Advanced precision drilling technology for complex industrial applications with exceptional accuracy and efficiency. Achieve the impossible with our world-class deep hole drilling solutions.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-3">
                <i className="ri-focus-3-line text-[#00D9C9] text-2xl"></i>
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-wider">Precision</div>
                  <div className="text-white font-bold">±0.005mm</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-3">
                <i className="ri-ruler-line text-[#00D9C9] text-2xl"></i>
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-wider">Max Depth</div>
                  <div className="text-white font-bold">3000mm</div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6 py-3">
                <i className="ri-compass-3-line text-[#00D9C9] text-2xl"></i>
                <div>
                  <div className="text-white/60 text-xs uppercase tracking-wider">Diameter Range</div>
                  <div className="text-white font-bold">φ0.5-30mm</div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <a
                href="#contact"
                className="group bg-[#00D9C9] text-[#001E3C] px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#00F5E0] transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Request Quote
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform w-6 h-6 flex items-center justify-center"></i>
              </a>
              <a
                href="#technologies"
                className="group bg-transparent text-white border-2 border-white/30 px-8 py-4 rounded-lg font-bold text-lg hover:border-[#00D9C9] hover:text-[#00D9C9] transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
              >
                Explore Technologies
                <i className="ri-arrow-down-line text-xl w-6 h-6 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-[#00D9C9] text-sm font-semibold tracking-wider uppercase">Our Capabilities</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#001E3C] mt-4 mb-6">
              Unmatched Precision &amp; Performance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Industry-leading deep hole drilling capabilities that set new standards for accuracy, efficiency, and reliability
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="group bg-gradient-to-br from-[#F8FFFE] to-white border border-gray-200 rounded-xl p-8 hover:border-[#00D9C9] hover:shadow-xl transition-all duration-300"
              >
                <div className="w-14 h-14 bg-[#00D9C9]/10 border border-[#00D9C9] rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${capability.icon} text-3xl text-[#00D9C9]`}></i>
                </div>

                <h3 className="text-xl font-bold text-[#001E3C] mb-3">
                  {capability.title}
                </h3>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {capability.description}
                </p>

                <div className="space-y-2">
                  {capability.specs.map((spec, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-[#00D9C9] text-sm mt-1"></i>
                      <span className="text-sm text-gray-700">{spec}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-24 bg-gradient-to-b from-[#F8FFFE] to-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-[#00D9C9] text-sm font-semibold tracking-wider uppercase">Drilling Methods</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#001E3C] mt-4 mb-6">
              Advanced Drilling Technologies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multiple drilling methods optimized for different applications, diameters, and depth requirements
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {technologies.map((tech, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl p-8 hover:border-[#00D9C9] hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#00D9C9] to-[#00B8A8] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${tech.icon} text-3xl text-white`}></i>
                </div>

                <h3 className="text-2xl font-bold text-[#001E3C] mb-4">
                  {tech.title}
                </h3>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {tech.description}
                </p>

                <div className="space-y-3">
                  {tech.advantages.map((advantage, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-[#00D9C9]/10 border border-[#00D9C9] rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-[#00D9C9] text-xs"></i>
                      </div>
                      <span className="text-sm text-gray-700">{advantage}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-[#00D9C9] text-sm font-semibold tracking-wider uppercase">Our Process</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#001E3C] mt-4 mb-6">
              From Setup to Inspection
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring consistent quality and optimal results for every project
            </p>
          </div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-gradient-to-r from-[#00D9C9]/20 via-[#00D9C9] to-[#00D9C9]/20"></div>

            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#00D9C9] to-[#00B8A8] rounded-full flex items-center justify-center mb-4 relative z-10 shadow-lg">
                      <span className="text-white text-xl font-bold">{step.step}</span>
                    </div>

                    <h3 className="text-lg font-bold text-[#001E3C] mb-3">
                      {step.title}
                    </h3>

                    <p className="text-sm text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-24 bg-gradient-to-b from-[#F8FFFE] to-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-[#00D9C9] text-sm font-semibold tracking-wider uppercase">Applications</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#001E3C] mt-4 mb-6">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trusted by leading manufacturers across diverse industries for critical deep hole drilling applications
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {applications.map((application, index) => (
              <div
                key={index}
                className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-[#00D9C9] hover:shadow-xl transition-all duration-300"
              >
                <div className="relative w-full h-64">
                  <img
                    src={application.image}
                    alt={application.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#001E3C] via-transparent to-transparent opacity-60"></div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#001E3C] mb-3">
                    {application.title}
                  </h3>

                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {application.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {application.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="bg-[#00D9C9]/10 text-[#00D9C9] text-xs font-semibold px-3 py-1 rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-[#001E3C] via-[#002A4E] to-[#001E3C] relative overflow-hidden">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#00D9C9]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#0066FF]/10 rounded-full blur-3xl"></div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-white/70 mb-12">
              Contact our technical team to discuss your deep hole drilling requirements and discover how we can help you achieve exceptional results.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="mailto:info@hi-tak.co.jp?subject=Deep Hole Drilling Inquiry"
                className="group bg-[#00D9C9] text-[#001E3C] px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#00F5E0] transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Contact Us
                <i className="ri-mail-line text-xl w-6 h-6 flex items-center justify-center"></i>
              </a>
              <Link
                to="/"
                className="group bg-transparent text-white border-2 border-white/30 px-8 py-4 rounded-lg font-bold text-lg hover:border-[#00D9C9] hover:text-[#00D9C9] transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
              >
                Back to Home
                <i className="ri-home-line text-xl w-6 h-6 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
